/**
 * Workspace Activity + Search list-surface continuity teaching harness
 * Access: /?test=workspace-activity
 *
 * Maater design-ui: Paper / Ink / Stone via shared `@/components/prototype/maater-proto-shell`.
 * List interaction standard (Browse folder view / document-list):
 *   - Single click: select; update preview if pane is open
 *   - Right click: select + open context menu (Preview is a menu action)
 *   - Toolbar PanelRight: pin/toggle secondary preview pane
 *   - Double click: open document (full tab)
 *   - Density: card vs list-columns (Search + Activity)
 *
 * Continuity: Activity / All edits is a Search named view, not a separate app.
 * Guide: docs/design/maater-list-surface-continuity.md
 */

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  Copy,
  Eye,
  FileText,
  Filter,
  LayoutGrid,
  LayoutList,
  PanelRight,
  RefreshCw,
  Search,
  Sparkles,
  Users,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import {
  MaaterProtoShell,
  MaaterProtoSegmentTrack,
  MAATER_PROTO_THEME_OPTIONS,
  type MaaterProtoThemeId,
} from '@/components/prototype/maater-proto-shell'

type ThemeId = MaaterProtoThemeId
type Density = 'card' | 'list'
type SearchView = 'search' | 'activity'
type Scene =
  | 'home-recent'
  | 'search-host'
  | 'search-activity'
  | 'search-activity-filtered'
  | 'saved-queries-rail'
  | 'search-user'
  | 'search-no-ws'

interface Member {
  id: string
  name: string
  email: string
  initials: string
  recentEdits: number
}

interface ActivityDoc {
  id: string
  title: string
  type: string
  location: string
  updatedAt: string
  authorId: string
  authorName: string
  excerpt: string
}

const CONTINUITY_GUIDE = 'docs/design/maater-list-surface-continuity.md'

const SCENES: { id: Scene; label: string; hint: string }[] = [
  {
    id: 'home-recent',
    label: 'Home Recent',
    hint: 'Widget with select/preview; All edits opens Search named view Activity',
  },
  {
    id: 'search-host',
    label: 'Search host',
    hint: 'Result list + PanelRight; breadcrumb Search; density toggle',
  },
  {
    id: 'search-activity',
    label: 'Activity named view',
    hint: 'Same Search chrome; Search | Activity switch; Members; breadcrumb Search > Activity',
  },
  {
    id: 'search-activity-filtered',
    label: 'Activity (Dan)',
    hint: 'Named view + Members filter (?user=dan)',
  },
  {
    id: 'saved-queries-rail',
    label: 'Saved queries rail',
    hint: 'Thin left rail: Views + Saved + Recent - experimental compare to inbox folders',
  },
  {
    id: 'search-user',
    label: 'Search + User',
    hint: 'User gated by workspace + same list interactions',
  },
  {
    id: 'search-no-ws',
    label: 'Search (no WS)',
    hint: 'User disabled without workspace',
  },
]

const MEMBERS: Member[] = [
  {
    id: 'rowan',
    name: 'Rowan Sentesy',
    email: 'rowan@hopstudios.com',
    initials: 'RS',
    recentEdits: 14,
  },
  {
    id: 'dan',
    name: 'The Danload',
    email: 'merkabaship@gmail.com',
    initials: 'TD',
    recentEdits: 3,
  },
  {
    id: 'travis',
    name: 'Travis Smith',
    email: 'nep@hopstudios.com',
    initials: 'TS',
    recentEdits: 0,
  },
]

const DOCS: ActivityDoc[] = [
  {
    id: 'd1',
    title: 'The Lightweb Brain: How It All Flows Together',
    type: 'Note',
    location: 'Product workspace /',
    updatedAt: '12m ago',
    authorId: 'dan',
    authorName: 'The Danload',
    excerpt:
      'Briefing for the organizing meetup. Five layers: Silicon, Models, Memory (Maater), Agency, Governance. Keys stay home, queries stay home - and memory stays home too.',
  },
  {
    id: 'd2',
    title: 'FlowNotes slots into the Lightweb at exactly the point where',
    type: 'Note',
    location: 'Product workspace /',
    updatedAt: '18m ago',
    authorId: 'dan',
    authorName: 'The Danload',
    excerpt:
      'GPU node delivers raw intelligence; Maater delivers memory, structure, and governance. MCP to LiteLLM, GitSource to Gitea, OIDC to Keycloak.',
  },
  {
    id: 'd3',
    title: 'The Kootenay Lightweb is a community services cooperative',
    type: 'Note',
    location: 'Product workspace /',
    updatedAt: '24m ago',
    authorId: 'dan',
    authorName: 'The Danload',
    excerpt:
      'Member-owned cloud, communications, and AI. Philosophy: build the better thing. Privacy as foundation. Good AI and the digital twin.',
  },
  {
    id: 'd4',
    title: 'Cursor Extension - URI scheme and sync',
    type: 'Note',
    location: 'Product workspace / product / bets',
    updatedAt: '2 days ago',
    authorId: 'rowan',
    authorName: 'Rowan Sentesy',
    excerpt:
      'Extension sync, materialize paths, and URI scheme for opening Maater docs from Cursor.',
  },
  {
    id: 'd5',
    title: 'Workspace soul formalization - QA hub',
    type: 'Note',
    location: 'Product workspace / implementations',
    updatedAt: '5 days ago',
    authorId: 'rowan',
    authorName: 'Rowan Sentesy',
    excerpt: 'QA hub and follow-ups for workspace soul formalization.',
  },
  {
    id: 'd6',
    title: 'Per-org BYO inference endpoint',
    type: 'Bet',
    location: 'Product workspace / product / bets',
    updatedAt: '1 week ago',
    authorId: 'rowan',
    authorName: 'Rowan Sentesy',
    excerpt: 'BYO inference for sovereign / self-hosted orgs.',
  },
]

function BreadcrumbTrail({
  crumbs,
  onCrumb,
}: {
  crumbs: { id: string; label: string }[]
  onCrumb?: (id: string) => void
}) {
  return (
    <nav className="flex min-w-0 items-center gap-1 text-xs" aria-label="Breadcrumb">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1
        return (
          <span key={c.id} className="inline-flex min-w-0 items-center gap-1">
            {i > 0 && (
              <ChevronRight
                className="h-3 w-3 shrink-0"
                style={{ color: 'var(--ink-faint)' }}
              />
            )}
            {last || !onCrumb ? (
              <span
                className={cn('truncate', last && 'font-medium')}
                style={{ color: last ? 'var(--ink)' : 'var(--ink-soft)' }}
              >
                {c.label}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onCrumb(c.id)}
                className="truncate hover:underline"
                style={{ color: 'var(--ink-soft)' }}
              >
                {c.label}
              </button>
            )}
          </span>
        )
      })}
    </nav>
  )
}

function DensityToggle({
  value,
  onChange,
}: {
  value: Density
  onChange: (d: Density) => void
}) {
  return (
    <div
      className="inline-flex rounded-md p-0.5"
      style={{ background: 'var(--paper-deep)' }}
      title="Density"
    >
      <button
        type="button"
        onClick={() => onChange('card')}
        className="flex h-7 w-7 items-center justify-center rounded"
        style={
          value === 'card'
            ? {
                background: 'var(--cream-card)',
                border: '1.5px solid var(--rust)',
                color: 'var(--rust)',
              }
            : { border: '1.5px solid transparent', color: 'var(--ink-soft)' }
        }
        title="Card density"
      >
        <LayoutGrid className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onChange('list')}
        className="flex h-7 w-7 items-center justify-center rounded"
        style={
          value === 'list'
            ? {
                background: 'var(--cream-card)',
                border: '1.5px solid var(--rust)',
                color: 'var(--rust)',
              }
            : { border: '1.5px solid transparent', color: 'var(--ink-soft)' }
        }
        title="List columns"
      >
        <LayoutList className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function MemberChip({
  member,
  active,
  onClick,
}: {
  member: Member
  active?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors"
      style={
        active
          ? {
              background: 'var(--cream-card)',
              border: '1.5px solid var(--rust)',
              color: 'var(--ink)',
            }
          : {
              background: 'var(--paper-raised)',
              border: '1px solid var(--line)',
              color: 'var(--ink-soft)',
            }
      }
    >
      <span
        className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold"
        style={{
          background: active ? 'var(--rust-pale)' : 'var(--paper-deep)',
          color: active ? 'var(--rust)' : 'var(--ink-soft)',
        }}
      >
        {member.initials}
      </span>
      <span className="font-medium" style={{ color: 'var(--ink)' }}>
        {member.name}
      </span>
      <span style={{ color: 'var(--ink-faint)' }}>{member.recentEdits}</span>
    </button>
  )
}

function PreviewPane({
  doc,
  onOpen,
  onClose,
}: {
  doc: ActivityDoc
  onOpen: () => void
  onClose: () => void
}) {
  return (
    <div
      className="flex h-full min-w-0 flex-col overflow-hidden"
      style={{ background: 'var(--paper)', borderLeft: '1px solid var(--line)' }}
    >
      <div
        className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2"
        style={{ borderColor: 'var(--line)' }}
      >
        <p className="truncate text-xs font-medium" style={{ color: 'var(--ink)' }}>
          {doc.title}
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onOpen}
            className="rounded-md px-2 py-1 text-xs font-medium"
            style={{
              background: 'var(--rust-pale)',
              color: 'var(--rust)',
              border: '1px solid color-mix(in oklch, var(--rust) 35%, transparent)',
            }}
          >
            Open
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md"
            style={{ color: 'var(--ink-soft)' }}
            title="Close preview"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="flex-1 space-y-3 overflow-auto p-4">
        <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
          {doc.type} · {doc.location}
        </p>
        <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
          Last edited {doc.updatedAt} by {doc.authorName}
        </p>
        <div
          className="rounded-md p-3 text-sm leading-relaxed"
          style={{
            background: 'var(--cream-card)',
            border: '1px solid var(--line)',
            color: 'var(--ink)',
          }}
        >
          {doc.excerpt}
        </div>
        <p className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>
          Mock preview body - production loads markdown via DocumentPreviewPane.
        </p>
      </div>
    </div>
  )
}

function ActivityRow({
  doc,
  selected,
  highlightAuthor,
  density,
  onSelect,
  onOpen,
  onContextMenu,
  onAuthorClick,
}: {
  doc: ActivityDoc
  selected?: boolean
  highlightAuthor?: boolean
  density: Density
  onSelect?: () => void
  onOpen?: () => void
  onContextMenu?: (e: React.MouseEvent) => void
  onAuthorClick?: (authorId: string) => void
}) {
  const selectedStyle: CSSProperties = {
    borderColor: selected ? 'var(--line)' : 'transparent',
    borderLeftColor: selected
      ? 'var(--rust)'
      : 'color-mix(in oklch, var(--rust) 40%, transparent)',
    background: selected ? 'var(--paper-raised)' : 'var(--cream-card)',
    boxShadow: selected
      ? 'inset 0 0 0 1px color-mix(in oklch, var(--rust) 25%, transparent)'
      : undefined,
    cursor: 'pointer',
  }

  if (density === 'list') {
    return (
      <div
        role="button"
        tabIndex={0}
        className="group grid w-full grid-cols-[minmax(0,1.4fr)_5.5rem_6.5rem_5.5rem] items-center gap-2 border border-l-2 px-3 py-2 text-left outline-none transition-colors"
        style={selectedStyle}
        onClick={() => onSelect?.()}
        onDoubleClick={(e) => {
          e.preventDefault()
          onOpen?.()
        }}
        onContextMenu={(e) => {
          e.preventDefault()
          onContextMenu?.(e)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onOpen?.()
          if (e.key === ' ') {
            e.preventDefault()
            onSelect?.()
          }
        }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <FileText
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: selected ? 'var(--rust)' : 'var(--ink-faint)' }}
          />
          <span className="truncate text-sm font-medium" style={{ color: 'var(--ink)' }}>
            {doc.title}
          </span>
        </div>
        <span className="truncate text-xs" style={{ color: 'var(--ink-faint)' }}>
          {doc.type}
        </span>
        <button
          type="button"
          className="truncate text-left text-xs underline-offset-2 hover:underline"
          style={{ color: highlightAuthor ? 'var(--rust)' : 'var(--ink-soft)' }}
          onClick={(e) => {
            e.stopPropagation()
            onAuthorClick?.(doc.authorId)
          }}
        >
          {doc.authorName}
        </button>
        <span
          className="inline-flex items-center justify-end gap-1 text-xs"
          style={{ color: 'var(--ink-faint)' }}
        >
          <Clock className="h-3 w-3" />
          {doc.updatedAt}
        </span>
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className="group w-full rounded-lg border border-l-2 p-3 text-left transition-colors outline-none"
      style={selectedStyle}
      onClick={() => onSelect?.()}
      onDoubleClick={(e) => {
        e.preventDefault()
        onOpen?.()
      }}
      onContextMenu={(e) => {
        e.preventDefault()
        onContextMenu?.(e)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen?.()
        if (e.key === ' ') {
          e.preventDefault()
          onSelect?.()
        }
      }}
    >
      <div className="flex items-start gap-3">
        <FileText
          className="mt-0.5 h-4 w-4 shrink-0"
          style={{ color: selected ? 'var(--rust)' : 'var(--ink-faint)' }}
        />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <h3
              className="truncate text-sm font-medium"
              style={{ color: 'var(--ink)' }}
            >
              {doc.title}
            </h3>
            <span className="shrink-0 text-xs" style={{ color: 'var(--ink-faint)' }}>
              {doc.type}
            </span>
          </div>
          <p className="mb-1 truncate text-xs" style={{ color: 'var(--ink-soft)' }}>
            {doc.location}
          </p>
          <div
            className="flex items-center gap-3 text-xs"
            style={{ color: 'var(--ink-faint)' }}
          >
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {doc.updatedAt}
            </span>
            <button
              type="button"
              className="underline-offset-2 hover:underline"
              style={{
                color: highlightAuthor ? 'var(--rust)' : 'var(--ink-soft)',
              }}
              onClick={(e) => {
                e.stopPropagation()
                onAuthorClick?.(doc.authorId)
              }}
            >
              {doc.authorName}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Shared list + secondary preview chrome (Browse folder / Search pattern). */
function ListSurface({
  docs,
  header,
  toolbarExtra,
  filterUserId,
  onAuthorClick,
  onHarnessNote,
  compact,
  density = 'card',
  onDensityChange,
  showDensityToggle,
  breadcrumbs,
  onBreadcrumb,
  modePanel,
}: {
  docs: ActivityDoc[]
  header: ReactNode
  toolbarExtra?: ReactNode
  filterUserId?: string | null
  onAuthorClick?: (id: string) => void
  onHarnessNote: (msg: string) => void
  compact?: boolean
  density?: Density
  onDensityChange?: (d: Density) => void
  showDensityToggle?: boolean
  breadcrumbs?: { id: string; label: string }[]
  onBreadcrumb?: (id: string) => void
  modePanel?: ReactNode
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    doc: ActivityDoc
  } | null>(null)

  const selected = docs.find((d) => d.id === selectedId) ?? null

  useEffect(() => {
    if (selectedId && !docs.some((d) => d.id === selectedId)) {
      setSelectedId(null)
    }
  }, [docs, selectedId])

  useEffect(() => {
    if (!contextMenu) return
    const close = () => setContextMenu(null)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [contextMenu])

  const selectDoc = (doc: ActivityDoc) => {
    setSelectedId(doc.id)
  }

  const openPreview = (doc: ActivityDoc) => {
    setSelectedId(doc.id)
    setPreviewOpen(true)
  }

  const openDoc = (doc: ActivityDoc) => {
    onHarnessNote(`Harness: open document "${doc.title}"`)
  }

  return (
    <div
      className={cn(
        'mx-auto flex overflow-hidden rounded-lg',
        compact ? 'max-w-3xl' : 'max-w-5xl',
        'h-[min(720px,80vh)] flex-col',
      )}
      style={{
        background: 'var(--cream-card)',
        border: '1px solid var(--line)',
      }}
    >
      {contextMenu && (
        <div
          className="fixed z-[200] min-w-[200px] rounded-md border py-1 text-sm shadow-md"
          style={{
            left: Math.min(contextMenu.x, window.innerWidth - 208),
            top: Math.min(contextMenu.y, window.innerHeight - 120),
            background: 'var(--paper)',
            borderColor: 'var(--line)',
            color: 'var(--ink)',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-black/5"
            onClick={() => {
              openDoc(contextMenu.doc)
              setContextMenu(null)
            }}
          >
            <FileText className="h-3.5 w-3.5 opacity-60" />
            Open
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-black/5"
            onClick={() => {
              openPreview(contextMenu.doc)
              setContextMenu(null)
            }}
          >
            <Eye className="h-3.5 w-3.5 opacity-60" />
            Preview
          </button>
          <div className="my-1 border-t" style={{ borderColor: 'var(--line)' }} />
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-black/5"
            onClick={() => {
              onHarnessNote(`Harness: copy path for "${contextMenu.doc.title}"`)
              setContextMenu(null)
            }}
          >
            <Copy className="h-3.5 w-3.5 opacity-60" />
            Copy path
          </button>
        </div>
      )}
      <div
        className="shrink-0 border-b"
        style={{ borderColor: 'var(--line)', background: 'var(--paper)' }}
      >
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div
            className="flex items-center gap-2 border-b px-4 py-2"
            style={{ borderColor: 'var(--line)' }}
          >
            <BreadcrumbTrail crumbs={breadcrumbs} onCrumb={onBreadcrumb} />
          </div>
        )}
        <div className="flex items-start justify-between gap-3 p-4 pb-3">
          <div className="min-w-0 flex-1">{header}</div>
          <div className="flex shrink-0 items-center gap-1">
            {toolbarExtra}
            {showDensityToggle && onDensityChange && (
              <DensityToggle value={density} onChange={onDensityChange} />
            )}
            <button
              type="button"
              onClick={() => setPreviewOpen((v) => !v)}
              className="flex h-8 w-8 items-center justify-center rounded-md"
              title={previewOpen ? 'Hide preview panel' : 'Show preview panel'}
              style={
                previewOpen
                  ? {
                      background: 'var(--rust-pale)',
                      border: '1.5px solid var(--rust)',
                      color: 'var(--rust)',
                    }
                  : {
                      background: 'transparent',
                      border: '1.5px solid transparent',
                      color: 'var(--ink-soft)',
                    }
              }
            >
              <PanelRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {modePanel}
        <div className="flex min-w-0 flex-1 flex-col">
          <div
            className={cn(
              'flex-1 overflow-auto p-4',
              density === 'list' ? 'space-y-0' : 'space-y-2',
            )}
          >
            {docs.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center gap-2 py-16 text-center"
                style={{ color: 'var(--ink-faint)' }}
              >
                <FileText className="h-10 w-10 opacity-50" />
                <p className="text-sm">No documents for this filter</p>
              </div>
            ) : (
              <>
                {density === 'list' && (
                  <div
                    className="mb-1 grid grid-cols-[minmax(0,1.4fr)_5.5rem_6.5rem_5.5rem] gap-2 px-3 text-[10px] font-medium uppercase tracking-wide"
                    style={{ color: 'var(--ink-faint)' }}
                  >
                    <span>Title</span>
                    <span>Type</span>
                    <span>Author</span>
                    <span className="text-right">Updated</span>
                  </div>
                )}
                {docs.map((doc) => (
                  <ActivityRow
                    key={doc.id}
                    doc={doc}
                    density={density}
                    selected={selectedId === doc.id}
                    highlightAuthor={filterUserId === doc.authorId}
                    onSelect={() => {
                      selectDoc(doc)
                      if (previewOpen) openPreview(doc)
                    }}
                    onOpen={() => openDoc(doc)}
                    onContextMenu={(e) => {
                      setSelectedId(doc.id)
                      setContextMenu({ x: e.clientX, y: e.clientY, doc })
                    }}
                    onAuthorClick={onAuthorClick}
                  />
                ))}
              </>
            )}
          </div>
          <ListSurfaceStatusBar includePreviewTip />
        </div>

        {previewOpen && selected && (
          <div className="w-[min(340px,42%)] shrink-0">
            <PreviewPane
              doc={selected}
              onOpen={() => openDoc(selected)}
              onClose={() => setPreviewOpen(false)}
            />
          </div>
        )}
        {previewOpen && !selected && (
          <div
            className="flex w-[min(340px,42%)] shrink-0 items-center justify-center border-l p-6 text-center text-xs"
            style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}
          >
            Select a document, or choose Preview from the row menu
          </div>
        )}
      </div>
    </div>
  )
}

function MembersStrip({
  filterUserId,
  onFilterUser,
  onClearFilter,
}: {
  filterUserId: string | null
  onFilterUser: (id: string) => void
  onClearFilter: () => void
}) {
  const activeMember = MEMBERS.find((m) => m.id === filterUserId)
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide"
          style={{ color: 'var(--ink-faint)' }}
        >
          <Users className="h-3.5 w-3.5" />
          Members
        </span>
        <button
          type="button"
          onClick={onClearFilter}
          className="rounded-md px-2.5 py-1.5 text-xs font-medium"
          style={
            !filterUserId
              ? {
                  background: 'var(--cream-card)',
                  border: '1.5px solid var(--rust)',
                  color: 'var(--ink)',
                }
              : {
                  background: 'var(--paper-raised)',
                  border: '1px solid var(--line)',
                  color: 'var(--ink-soft)',
                }
          }
        >
          Everyone
        </button>
        {MEMBERS.map((m) => (
          <MemberChip
            key={m.id}
            member={m}
            active={filterUserId === m.id}
            onClick={() => onFilterUser(m.id)}
          />
        ))}
      </div>
      {filterUserId && (
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs"
            style={{
              background: 'var(--rust-pale)',
              color: 'var(--rust)',
              border: '1px solid color-mix(in oklch, var(--rust) 30%, transparent)',
            }}
          >
            Edited by {activeMember?.name}
            <button type="button" onClick={onClearFilter} aria-label="Clear filter">
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      )}
    </div>
  )
}

function SceneHomeRecent({
  onAllEdits,
  onHarnessNote,
}: {
  onAllEdits: () => void
  onHarnessNote: (msg: string) => void
}) {
  return (
    <ListSurface
      docs={DOCS.slice(0, 4)}
      compact
      density="card"
      onHarnessNote={onHarnessNote}
      header={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2
            className="text-lg font-semibold"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              color: 'var(--ink)',
            }}
          >
            Recent Activity
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md"
              style={{ color: 'var(--ink-soft)' }}
              title="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onAllEdits}
              className="text-xs font-medium"
              style={{ color: 'var(--rust)' }}
            >
              All edits
            </button>
          </div>
        </div>
      }
    />
  )
}

/** Search host + Activity named view - same chrome, different view preset. */
function SceneSearchSurface({
  view,
  onViewChange,
  filterUserId,
  onFilterUser,
  onClearFilter,
  workspaceSelected,
  userId,
  onUserChange,
  onToggleWorkspace,
  showUserFilter,
  density,
  onDensityChange,
  modePanel,
  onHarnessNote,
  onBreadcrumbSearch,
}: {
  view: SearchView
  onViewChange: (v: SearchView) => void
  filterUserId: string | null
  onFilterUser: (id: string) => void
  onClearFilter: () => void
  workspaceSelected?: boolean
  userId?: string | null
  onUserChange?: (id: string | null) => void
  onToggleWorkspace?: () => void
  showUserFilter?: boolean
  density: Density
  onDensityChange: (d: Density) => void
  modePanel?: ReactNode
  onHarnessNote: (msg: string) => void
  onBreadcrumbSearch?: () => void
}) {
  const [query, setQuery] = useState(view === 'search' ? 'lightweb' : '')
  const membersAvailable = workspaceSelected ? MEMBERS : []

  useEffect(() => {
    if (view === 'search') {
      setQuery((q) => (q.trim() ? q : 'lightweb'))
    }
  }, [view])

  const searchDocs = useMemo(() => {
    if (view === 'activity') {
      return filterUserId ? DOCS.filter((d) => d.authorId === filterUserId) : DOCS
    }
    let list = DOCS.filter(
      (d) =>
        !query.trim() ||
        d.title.toLowerCase().includes(query.toLowerCase()) ||
        d.location.toLowerCase().includes(query.toLowerCase()) ||
        d.excerpt.toLowerCase().includes(query.toLowerCase()),
    )
    if (userId) list = list.filter((d) => d.authorId === userId)
    if (!query.trim() && !showUserFilter) return []
    return list
  }, [view, query, filterUserId, userId, showUserFilter])

  const crumbs =
    view === 'activity'
      ? [
          { id: 'search', label: 'Search' },
          { id: 'activity', label: 'Activity' },
        ]
      : [{ id: 'search', label: 'Search' }]

  const activeMember = MEMBERS.find((m) => m.id === filterUserId)

  return (
    <ListSurface
      docs={searchDocs}
      filterUserId={view === 'activity' ? filterUserId : userId}
      onAuthorClick={view === 'activity' ? onFilterUser : undefined}
      onHarnessNote={onHarnessNote}
      density={density}
      onDensityChange={onDensityChange}
      showDensityToggle
      breadcrumbs={crumbs}
      onBreadcrumb={(id) => {
        if (id === 'search') {
          onViewChange('search')
          onBreadcrumbSearch?.()
        }
      }}
      modePanel={modePanel}
      header={
        <div className="space-y-3">
          {!showUserFilter && (
            <div className="flex flex-wrap items-center gap-2">
              <MaaterProtoSegmentTrack
                value={view}
                onChange={(id) => onViewChange(id as SearchView)}
                options={[
                  { id: 'search', label: 'Search' },
                  { id: 'activity', label: 'Activity' },
                ]}
              />
              <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>
                Named views on Search host - not a separate Activity app
              </span>
            </div>
          )}

          {view === 'search' && (
            <>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    style={{ color: 'var(--ink-faint)' }}
                  />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search files, notes, todos..."
                    className="h-9 w-full rounded-md border pl-9 pr-4 text-sm outline-none"
                    style={{
                      background: 'var(--cream-card)',
                      borderColor: 'var(--line)',
                      color: 'var(--ink)',
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border px-3 text-xs font-medium"
                  style={{
                    background: 'var(--paper-raised)',
                    borderColor: 'var(--line)',
                    color: 'var(--ink-soft)',
                  }}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Keyword
                </button>
              </div>
              {showUserFilter && (
                <div className="flex flex-wrap items-center gap-2">
                  <FakeSelect label="Type" />
                  <FakeSelect label="Date" />
                  <button
                    type="button"
                    onClick={onToggleWorkspace}
                    className="inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs"
                    style={
                      workspaceSelected
                        ? {
                            background: 'var(--cream-card)',
                            borderColor: 'var(--rust)',
                            color: 'var(--ink)',
                          }
                        : {
                            background: 'var(--paper-raised)',
                            borderColor: 'var(--line)',
                            color: 'var(--ink-soft)',
                          }
                    }
                  >
                    <span
                      className="inline-block h-3 w-1 rounded-sm"
                      style={{ background: 'var(--rust)' }}
                    />
                    {workspaceSelected ? 'Product workspace' : 'No workspace'}
                  </button>
                  <select
                    className="h-7 rounded-md border px-2 text-xs outline-none disabled:opacity-50"
                    style={{
                      background: workspaceSelected
                        ? 'var(--cream-card)'
                        : 'var(--paper-deep)',
                      borderColor: workspaceSelected ? 'var(--rust)' : 'var(--line)',
                      color: 'var(--ink)',
                      minWidth: '8.5rem',
                    }}
                    disabled={!workspaceSelected}
                    value={userId ?? ''}
                    onChange={(e) => onUserChange?.(e.target.value || null)}
                  >
                    <option value="">
                      {workspaceSelected ? 'User (any)' : 'User (select workspace)'}
                    </option>
                    {membersAvailable.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  <FakeSelect label="Status" />
                </div>
              )}
              {!showUserFilter && (
                <div className="flex flex-wrap items-center gap-2">
                  <FakeSelect label="Type" />
                  <FakeSelect label="Date" />
                  <FakeSelect label="Status" />
                </div>
              )}
              {showUserFilter && !workspaceSelected && (
                <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
                  User filter is gated by workspace scope.
                </p>
              )}
              {(query.trim() || showUserFilter) && (
                <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
                  {searchDocs.length} result{searchDocs.length !== 1 ? 's' : ''}
                  {userId
                    ? ` · edited by ${MEMBERS.find((m) => m.id === userId)?.name}`
                    : ''}
                </p>
              )}
            </>
          )}

          {view === 'activity' && (
            <>
              <div>
                <h1
                  className="text-xl font-semibold tracking-tight"
                  style={{
                    fontFamily: 'Fraunces, Georgia, serif',
                    color: 'var(--ink)',
                  }}
                >
                  Activity
                </h1>
                <p className="mt-1 text-sm" style={{ color: 'var(--ink-soft)' }}>
                  Product workspace
                  {searchDocs.length > 0 ? ` - ${searchDocs.length} documents` : ''}
                  {activeMember ? ` - edited by ${activeMember.name}` : ''}
                  <span className="ml-1 text-xs" style={{ color: 'var(--ink-faint)' }}>
                    · /search?view=activity
                  </span>
                </p>
              </div>
              <MembersStrip
                filterUserId={filterUserId}
                onFilterUser={onFilterUser}
                onClearFilter={onClearFilter}
              />
            </>
          )}
        </div>
      }
      toolbarExtra={
        view === 'activity' ? (
          <span
            className="mr-1 hidden items-center gap-1 text-[10px] sm:inline-flex"
            style={{ color: 'var(--ink-faint)' }}
          >
            <Filter className="h-3 w-3" />
            Type later
          </span>
        ) : undefined
      }
    />
  )
}

function SavedQueriesRail({
  activeView,
  onSelectView,
}: {
  activeView: SearchView
  onSelectView: (v: SearchView) => void
}) {
  const railBtn = (id: SearchView | 'saved' | 'recent', label: string, active: boolean) => (
    <button
      key={id}
      type="button"
      onClick={() => {
        if (id === 'search' || id === 'activity') onSelectView(id)
      }}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs"
      style={
        active
          ? {
              background: 'var(--paper-raised)',
              borderLeft: '2px solid var(--rust)',
              color: 'var(--ink)',
              fontWeight: 600,
            }
          : {
              borderLeft: '2px solid transparent',
              color: 'var(--ink-soft)',
            }
      }
    >
      {label}
    </button>
  )

  return (
    <aside
      className="flex w-[148px] shrink-0 flex-col gap-3 border-r px-2 py-3"
      style={{ borderColor: 'var(--line)', background: 'var(--paper)' }}
    >
      <div>
        <p
          className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--ink-faint)' }}
        >
          Views
        </p>
        {railBtn('search', 'Search', activeView === 'search')}
        {railBtn('activity', 'Activity', activeView === 'activity')}
      </div>
      <div>
        <p
          className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--ink-faint)' }}
        >
          Saved
        </p>
        {railBtn('saved', 'Lightweb notes', false)}
        {railBtn('saved', 'Bets this week', false)}
      </div>
      <div>
        <p
          className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--ink-faint)' }}
        >
          Recent
        </p>
        {railBtn('recent', 'soul formalization', false)}
        {railBtn('recent', 'BYO inference', false)}
      </div>
      <p
        className="mt-auto px-2 text-[9px] leading-snug"
        style={{ color: 'var(--ink-faint)' }}
      >
        Experimental - compare to inbox folders rail (nouns in mode panel, items in main).
      </p>
    </aside>
  )
}

function FakeSelect({ label }: { label: string }) {
  return (
    <div
      className="flex h-7 w-24 items-center rounded-md border px-2 text-xs"
      style={{
        background: 'var(--paper-raised)',
        borderColor: 'var(--line)',
        color: 'var(--ink-faint)',
      }}
    >
      {label}
    </div>
  )
}

/** Prototype harness for Search host + Activity named view continuity. */
export function TestWorkspaceActivity() {
  const [theme, setTheme] = useState<ThemeId>('paper')
  const [scene, setScene] = useState<Scene>('search-activity')
  const [filterUserId, setFilterUserId] = useState<string | null>(null)
  const [searchUserId, setSearchUserId] = useState<string | null>('dan')
  const [workspaceOn, setWorkspaceOn] = useState(true)
  const [density, setDensity] = useState<Density>('card')
  const [railView, setRailView] = useState<SearchView>('activity')
  const [harnessNote, setHarnessNote] = useState<string | null>(null)

  const meta = SCENES.find((s) => s.id === scene)

  useEffect(() => {
    if (!harnessNote) return
    const t = window.setTimeout(() => setHarnessNote(null), 2800)
    return () => window.clearTimeout(t)
  }, [harnessNote])

  const goScene = (next: Scene) => {
    setScene(next)
    if (next === 'search-activity-filtered') setFilterUserId('dan')
    if (next === 'search-activity' || next === 'search-host') setFilterUserId(null)
    if (next === 'saved-queries-rail') {
      setFilterUserId(null)
      setRailView('activity')
    }
    if (next === 'search-user') {
      setWorkspaceOn(true)
      setSearchUserId('dan')
    }
    if (next === 'search-no-ws') {
      setWorkspaceOn(false)
      setSearchUserId(null)
    }
  }

  const surfaceView = ((): SearchView => {
    if (scene === 'search-host') return 'search'
    if (scene === 'search-user' || scene === 'search-no-ws') return 'search'
    if (scene === 'saved-queries-rail') return railView
    return 'activity'
  })()

  const showSearchSurface =
    scene === 'search-host' ||
    scene === 'search-activity' ||
    scene === 'search-activity-filtered' ||
    scene === 'saved-queries-rail' ||
    scene === 'search-user' ||
    scene === 'search-no-ws'

  const handleViewChange = (v: SearchView) => {
    if (scene === 'saved-queries-rail') {
      setRailView(v)
      if (v === 'search') setFilterUserId(null)
      return
    }
    if (scene === 'search-user' || scene === 'search-no-ws') return
    if (v === 'search') goScene('search-host')
    else goScene(filterUserId ? 'search-activity-filtered' : 'search-activity')
  }

  return (
    <MaaterProtoShell theme={theme}>
      <div className="mx-auto max-w-5xl space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <a
              href="/?test=index"
              className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium"
              style={{ color: 'var(--ink-soft)' }}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Prototype index
            </a>
            <h1
              className="text-xl font-semibold tracking-tight"
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                color: 'var(--ink)',
              }}
            >
              Search + Activity continuity
            </h1>
            <p className="mt-1 max-w-xl text-sm" style={{ color: 'var(--ink-soft)' }}>
              Activity / All edits as a Search named view. Standard list gestures,
              density toggle, PanelRight preview. Paper / Ink / Stone.
            </p>
          </div>
          <div className="space-y-2">
            <p
              className="text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--ink-faint)' }}
            >
              Theme
            </p>
            <MaaterProtoSegmentTrack
              value={theme}
              onChange={(id) => setTheme(id as ThemeId)}
              options={MAATER_PROTO_THEME_OPTIONS}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {SCENES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goScene(s.id)}
              className="rounded-md px-3 py-1.5 text-xs font-medium"
              style={
                scene === s.id
                  ? {
                      background: 'var(--cream-card)',
                      border: '1.5px solid var(--rust)',
                      color: 'var(--ink)',
                    }
                  : {
                      background: 'var(--paper-raised)',
                      border: '1px solid var(--line)',
                      color: 'var(--ink-soft)',
                    }
              }
            >
              {s.label}
            </button>
          ))}
        </div>

        {meta && (
          <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
            {meta.hint}
          </p>
        )}

        {harnessNote && (
          <div
            className="rounded-md px-3 py-2 text-xs font-medium"
            style={{
              background: 'var(--rust-pale)',
              color: 'var(--rust)',
              border: '1px solid color-mix(in oklch, var(--rust) 30%, transparent)',
            }}
          >
            {harnessNote}
          </div>
        )}

        {scene === 'home-recent' && (
          <SceneHomeRecent
            onAllEdits={() => goScene('search-activity')}
            onHarnessNote={setHarnessNote}
          />
        )}

        {showSearchSurface && (
          <SceneSearchSurface
            key={scene}
            view={surfaceView}
            onViewChange={handleViewChange}
            filterUserId={filterUserId}
            onFilterUser={(id) => {
              setFilterUserId(id)
              if (scene === 'saved-queries-rail') setRailView('activity')
              else setScene('search-activity-filtered')
            }}
            onClearFilter={() => {
              setFilterUserId(null)
              if (scene !== 'saved-queries-rail') setScene('search-activity')
            }}
            workspaceSelected={workspaceOn}
            userId={searchUserId}
            onUserChange={setSearchUserId}
            onToggleWorkspace={() => {
              setWorkspaceOn((v) => {
                const next = !v
                if (!next) setSearchUserId(null)
                setScene(next ? 'search-user' : 'search-no-ws')
                return next
              })
            }}
            showUserFilter={scene === 'search-user' || scene === 'search-no-ws'}
            density={density}
            onDensityChange={setDensity}
            modePanel={
              scene === 'saved-queries-rail' ? (
                <SavedQueriesRail
                  activeView={railView}
                  onSelectView={(v) => {
                    setRailView(v)
                    if (v === 'search') setFilterUserId(null)
                  }}
                />
              ) : undefined
            }
            onHarnessNote={setHarnessNote}
            onBreadcrumbSearch={() => handleViewChange('search')}
          />
        )}

        <ul
          className="list-disc space-y-1 pl-5 text-xs"
          style={{ color: 'var(--ink-faint)' }}
        >
          <li>
            Standard list chrome matches Browse folder view: select, right-click
            preview, PanelRight toggle, double-click open, density card vs list.
          </li>
          <li>
            Activity / All edits is a Search named view (`/search?view=activity`), not
            a parallel app. Home All edits switches to that scene.
          </li>
          <li>
            Continuity guide:{' '}
            <span className="font-mono" style={{ color: 'var(--ink-soft)' }}>
              {CONTINUITY_GUIDE}
            </span>
          </li>
        </ul>
      </div>
    </MaaterProtoShell>
  )
}
